import React, { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useToast } from "../../admin/components/ToastProvider";
import { tokens as t } from "../../theme/tokens";
import { EmployeeSurface } from "./EmployeePage";

export type Score = { result: number; total: number };

/** Matches the scoring rules used by the existing quarterly and annual reports. */
export const calculateScore = (items: any[]): Score =>
  items.reduce(
    (score, item) => {
      switch (item.surveyquestiontypename) {
        case "Rank":
          score.total += (item.surveyquestionrankdescription || "").split(",").length;
          score.result += item.AnswerValue || 0;
          break;
        case "YesNo":
          score.total += 5;
          score.result += item.AnswerText === "Yes" ? 5 : 1;
          break;
        case "Dropdown": {
          const optionCount = (item.surveyquestionprefilledoptions || "").split(";").length;
          score.total += optionCount;
          score.result += item.AnswerText ? optionCount : 1;
          break;
        }
        case "Multiselect": {
          const optionCount = (item.surveyquestionprefilledoptions || "").split(";").length;
          score.total += optionCount;
          score.result += item.AnswerText ? item.AnswerText.split(";").length : 0;
          break;
        }
        default:
          break;
      }
      return score;
    },
    { result: 0, total: 0 }
  );

export const ScoreBar: React.FC<{
  label: string;
  score: Score;
  displayScore?: Score;
  emphasized?: boolean;
}> = ({ label, score, displayScore = score, emphasized = false }) => {
  const percentage = score.total ? Math.min(100, Math.max(0, (score.result / score.total) * 100)) : 0;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 0.35fr) minmax(220px, 1fr) auto" },
        alignItems: "center",
        gap: { xs: 1, sm: 2 },
        px: { xs: 2, sm: 2.5 },
        py: emphasized ? 2.25 : 1.75,
        bgcolor: emphasized ? t.color.mint : t.color.paper,
        borderTop: `1px solid ${emphasized ? t.color.mintLine : t.color.rowLine}`,
      }}
    >
      <Typography sx={{ fontWeight: emphasized ? 700 : 600 }}>{label}</Typography>
      <LinearProgress
        variant="determinate"
        value={percentage}
        aria-label={`${label}: ${displayScore.result} out of ${displayScore.total}`}
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: emphasized ? t.color.paper : t.color.canvas,
          "& .MuiLinearProgress-bar": { borderRadius: 5 },
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: 700, color: t.color.data, whiteSpace: "nowrap" }}>
        {displayScore.result} / {displayScore.total}
      </Typography>
    </Box>
  );
};

export const ScoreOverview: React.FC<{
  rows: Array<{ key: string; label: string; score: Score; displayScore?: Score }>;
  overall: Score;
  overallDisplay?: Score;
}> = ({ rows, overall, overallDisplay }) => (
  <EmployeeSurface>
    <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25 }}>
      <Typography component="h2" variant="h6">Score overview</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        A summary of the ratings in this performance report.
      </Typography>
    </Box>
    <ScoreBar label="Overall" score={overall} displayScore={overallDisplay} emphasized />
    {rows.map((row) => (
      <ScoreBar key={row.key} label={row.label} score={row.score} displayScore={row.displayScore} />
    ))}
  </EmployeeSurface>
);

export const ReportSection: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
  id?: string;
}> = ({ title, description, children, id }) => (
  <EmployeeSurface id={id}>
    <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25, borderBottom: `1px solid ${t.color.cardLine}` }}>
      <Typography component="h2" variant="h6">{title}</Typography>
      {description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>}
    </Box>
    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>{children}</Box>
  </EmployeeSurface>
);

const pointsFor = (item: any) => calculateScore([item]);

export const QuestionResult: React.FC<{ item: any; period?: string }> = ({ item, period }) => {
  const score = pointsFor(item);
  const selected = item.AnswerText ? item.AnswerText.split(";").filter(Boolean) : [];
  const allOptions = item.surveyquestionprefilledoptions
    ? item.surveyquestionprefilledoptions.split(";").map((option: string) => option.trim()).filter(Boolean)
    : [];
  const rankOptions = item.surveyquestionrankdescription
    ? item.surveyquestionrankdescription.split(",")
    : [];
  const answerLabel =
    item.surveyquestiontypename === "Rank"
      ? rankOptions[(item.AnswerValue || 0) - 1] || "No response"
      : item.surveyquestiontypename === "YesNo"
      ? item.AnswerText === "Yes" ? "Yes" : "No"
      : item.surveyquestiontypename === "Dropdown"
      ? item.AnswerText || "No response"
      : "";

  return (
    <Box sx={{ py: 2, "& + &": { borderTop: `1px solid ${t.color.rowLine}` } }}>
      {period && (
        <Typography variant="overline" sx={{ color: t.color.primary }}>{period}</Typography>
      )}
      <Typography
        component="h3"
        variant="body1"
        sx={{ fontWeight: 600, "& p": { m: 0 } }}
        dangerouslySetInnerHTML={{ __html: item.surveyquestiontext || "" }}
      />
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.25 }}>
        <Chip label={`${score.result} of ${score.total} points`} color="success" size="small" />
        {answerLabel && <Chip label={answerLabel} variant="outlined" size="small" />}
        {item.surveyquestiontypename === "Multiselect" && selected.map((answer: string) => (
          <Chip key={answer} label={answer} variant="outlined" size="small" />
        ))}
      </Stack>
      {item.surveyquestiontypename === "Multiselect" && allOptions.some((option: string) => !selected.includes(option)) && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Not selected: {allOptions.filter((option: string) => !selected.includes(option)).join(", ")}
        </Typography>
      )}
    </Box>
  );
};

export const CommentList: React.FC<{ comments: Array<string | null | undefined>; emptyLabel?: string }> = ({
  comments,
  emptyLabel = "No comments were provided.",
}) => {
  const lines = comments.flatMap((comment) => (comment || "").split("\n")).filter((line) => Boolean(line.trim()));
  if (!lines.length) return <Typography color="text.secondary">{emptyLabel}</Typography>;

  return (
    <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.5 }}>
      {lines.map((line, index) => <Typography component="li" key={`${line}-${index}`}>{line}</Typography>)}
    </Stack>
  );
};

export const AcknowledgementCard: React.FC<{
  date: string | null;
  comment: string;
  managerView: boolean;
  onSubmit: (comment: string) => Promise<unknown>;
}> = ({ date, comment, managerView, onSubmit }) => {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const acknowledgedAt = date || submittedAt;

  const acknowledgementText = useMemo(() => {
    if (!acknowledgedAt) return "";
    const parsed = new Date(acknowledgedAt);
    return `${parsed.toLocaleDateString()} at ${parsed.toLocaleTimeString()}`;
  }, [acknowledgedAt]);

  const submit = () => {
    setSubmitting(true);
    onSubmit(value)
      .then(() => {
        setSubmittedAt(new Date().toISOString());
        setOpen(false);
        showToast("Performance report acknowledged.");
      })
      .catch(() => showToast("We could not save your acknowledgement. Please try again.", "error"))
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <EmployeeSurface>
        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Typography component="h2" variant="h6">Receipt acknowledgement</Typography>
          {acknowledgedAt ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              Acknowledged on {acknowledgementText}.
              <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                Comments: {date ? comment : value || "No comments provided"}
              </Box>
            </Alert>
          ) : managerView ? (
            <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>The employee has not acknowledged this report.</Alert>
          ) : (
            <Box sx={{ display: "flex", alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 2, mt: 1.5 }}>
              <Typography color="text.secondary">I acknowledge that I have received and reviewed this performance report.</Typography>
              <Button variant="contained" onClick={() => setOpen(true)}>Acknowledge report</Button>
            </Box>
          )}
        </Box>
      </EmployeeSurface>

      <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="sm" aria-labelledby="acknowledgement-title">
        <DialogTitle id="acknowledgement-title">Acknowledge performance report</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Your acknowledgement confirms that you received and reviewed this report. You may include optional comments below.
          </DialogContentText>
          <TextField
            label="Comments (optional)"
            multiline
            minRows={4}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>{submitting ? "Saving…" : "Save acknowledgement"}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
